import React, { useEffect, useMemo, useState } from 'react'
import {
	Box,
	CircularProgress,
	Modal,
	Stack,
	Tab,
	Tabs,
	TextField,
	Typography,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import {
	forceUpdateUserPosition,
	searchGooglePlace,
} from 'src/apis/userAPI'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { color } from 'src/variable/constant'

const modalStyle = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: 'min(720px, calc(100vw - 32px))',
	maxHeight: 'calc(100vh - 48px)',
	overflow: 'auto',
	bgcolor: 'background.paper',
	boxShadow: 24,
	borderRadius: '8px',
	p: 3,
	outline: 'none',
}

const isValidCoordinate = (latitude: number, longitude: number) =>
	Number.isFinite(latitude) &&
	Number.isFinite(longitude) &&
	latitude !== 0 &&
	longitude !== 0

function ModalUpdateUserAddress({ open, user, onClose, onSuccess }) {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [tab, setTab] = useState(0)
	const [searchText, setSearchText] = useState('')
	const [searching, setSearching] = useState(false)
	const [results, setResults] = useState([])
	const [selectedPlace, setSelectedPlace] = useState(null)
	const [latitude, setLatitude] = useState('')
	const [longitude, setLongitude] = useState('')

	useEffect(() => {
		if (!open) return

		setTab(0)
		setSearchText('')
		setSearching(false)
		setResults([])
		setSelectedPlace(null)
		setLatitude(user?.latitude ? String(user.latitude) : '')
		setLongitude(user?.longitude ? String(user.longitude) : '')
	}, [open, user])

	const selectedCoordinates = useMemo(() => {
		if (tab === 0 && selectedPlace?.geometry?.location) {
			return {
				latitude: Number(selectedPlace.geometry.location.lat),
				longitude: Number(selectedPlace.geometry.location.lng),
			}
		}

		return {
			latitude: Number(latitude),
			longitude: Number(longitude),
		}
	}, [latitude, longitude, selectedPlace, tab])

	const handleSearch = async () => {
		const text = searchText.trim()
		if (!text) {
			openError(new Error('Please enter address text to search'))
			return
		}

		setSearching(true)
		setSelectedPlace(null)
		try {
			const res = await searchGooglePlace({
				text,
				language: 'en',
			})
			const data = res?.data?.results?.object || res?.data?.results || res?.data
			setResults(data?.results || [])
		} catch (error) {
			openError(error)
		} finally {
			setSearching(false)
		}
	}

	const handleSelectPlace = (place) => {
		const lat = place?.geometry?.location?.lat
		const lng = place?.geometry?.location?.lng

		if (!isValidCoordinate(Number(lat), Number(lng))) {
			openError(new Error('Selected place does not have valid latitude and longitude'))
			return
		}

		setSelectedPlace(place)
		setLatitude(String(lat))
		setLongitude(String(lng))
	}

	const handleSubmit = async () => {
		const nextLatitude = selectedCoordinates.latitude
		const nextLongitude = selectedCoordinates.longitude

		if (!isValidCoordinate(nextLatitude, nextLongitude)) {
			openError(new Error('Please select a place or enter valid latitude and longitude'))
			return
		}

		openLoadingContext()
		try {
			const res = await forceUpdateUserPosition(user.id, {
				latitude: nextLatitude,
				longitude: nextLongitude,
			})

			if (res?.data?.code === 200) {
				onSuccess(res.data?.results?.object)
				onClose()
				openModal({
					type: 'success',
					label: 'User address has been updated successfully',
				})
			}
		} catch (error) {
			openError(error)
		} finally {
			closeLoadingContext()
		}
	}

	return (
		<Modal open={open} onClose={onClose}>
			<Box sx={modalStyle}>
				<Stack spacing={2}>
					<Stack direction="row" justifyContent="space-between" alignItems="center">
						<Box>
							<Typography variant="h6">Update user address</Typography>
							<Typography sx={{ color: '#6b7280', fontSize: 13 }}>
								{user?.name || user?.phone || user?.id}
							</Typography>
						</Box>
						<Close sx={{ cursor: 'pointer' }} onClick={onClose} />
					</Stack>

					<Tabs value={tab} onChange={(_, value) => setTab(value)}>
						<Tab label="Search address" />
						<Tab label="Manual coordinates" />
					</Tabs>

					{tab === 0 && (
						<Stack spacing={2}>
							<Stack direction="row" spacing={1}>
								<TextField
									size="small"
									fullWidth
									label="Address"
									value={searchText}
									onChange={(event) => setSearchText(event.target.value)}
									onKeyDown={(event) => {
										if (event.key === 'Enter') {
											event.preventDefault()
											handleSearch()
										}
									}}
								/>
								<CustomButton
									variant="outlined"
									sx={{ minWidth: 100 }}
									onClick={handleSearch}
									disabled={searching}
								>
									Search
								</CustomButton>
							</Stack>

							<Stack spacing={1} sx={{ maxHeight: 300, overflow: 'auto' }}>
								{searching && (
									<Stack alignItems="center" sx={{ py: 3 }}>
										<CircularProgress size={28} />
									</Stack>
								)}

								{!searching &&
									results.map((place) => {
										const isSelected = selectedPlace?.place_id === place.place_id
										const hasCoordinates = !!place?.geometry?.location
										return (
											<Box
												key={place.place_id || place.formatted_address}
												onClick={() => hasCoordinates && handleSelectPlace(place)}
												sx={{
													border: `1px solid ${
														isSelected ? color.CmsPrimary : '#e5e7eb'
													}`,
													borderRadius: '6px',
													p: 1.25,
													cursor: hasCoordinates ? 'pointer' : 'not-allowed',
													opacity: hasCoordinates ? 1 : 0.5,
													background: isSelected ? '#f0fdf4' : 'white',
												}}
											>
												<Typography sx={{ fontWeight: 600 }}>
													{place.name || place.formatted_address}
												</Typography>
												<Typography sx={{ color: '#6b7280', fontSize: 13 }}>
													{place.formatted_address}
												</Typography>
											</Box>
										)
									})}

								{!searching && searchText && !results.length && (
									<Typography sx={{ color: '#6b7280', textAlign: 'center', py: 2 }}>
										No address results
									</Typography>
								)}
							</Stack>
						</Stack>
					)}

					{tab === 1 && (
						<Stack spacing={2}>
							<TextField
								size="small"
								type="number"
								label="Latitude"
								value={latitude}
								onChange={(event) => setLatitude(event.target.value)}
								fullWidth
							/>
							<TextField
								size="small"
								type="number"
								label="Longitude"
								value={longitude}
								onChange={(event) => setLongitude(event.target.value)}
								fullWidth
							/>
						</Stack>
					)}

					<Stack spacing={0.5}>
						<Typography sx={{ fontWeight: 600 }}>Selected coordinates</Typography>
						<Typography sx={{ color: '#6b7280', fontSize: 13 }}>
							Latitude: {Number.isFinite(selectedCoordinates.latitude) ? selectedCoordinates.latitude : '-'}
						</Typography>
						<Typography sx={{ color: '#6b7280', fontSize: 13 }}>
							Longitude: {Number.isFinite(selectedCoordinates.longitude) ? selectedCoordinates.longitude : '-'}
						</Typography>
					</Stack>

					<Stack direction="row" spacing={2} justifyContent="flex-end">
						<CustomButton variant="outlined" onClick={onClose}>
							Cancel
						</CustomButton>
						<CustomButton variant="outlined" onClick={handleSubmit}>
							Submit
						</CustomButton>
					</Stack>
				</Stack>
			</Box>
		</Modal>
	)
}

export default React.memo(ModalUpdateUserAddress)
